/**
 * Screen 07 — locked out.
 *
 * Driven by a REAL GoTrue rate-limit response (HTTP 429), not by a client-side attempt
 * counter. A counter in the browser is trivially reset by a reload, so a screen based on
 * one is decoration: it tells an honest user they are locked out while doing nothing to an
 * attacker.
 *
 * The wait is stated because a lockout with no stated duration produces a support ticket
 * every single time.
 */
import { useEffect, useState, type ReactNode } from 'react';

export function LockedScreen({
  email,
  retryAfterSeconds,
  onBack,
}: {
  email: string;
  /** From GoTrue's `Retry-After`, when it sends one. */
  retryAfterSeconds: number | null;
  onBack: () => void;
}): ReactNode {
  const [left, setLeft] = useState(retryAfterSeconds ?? 0);

  useEffect(() => {
    if (left <= 0) return;
    const id = window.setInterval(() => setLeft((n) => Math.max(0, n - 1)), 1000);
    return () => window.clearInterval(id);
  }, [left]);

  return (
    <section style={{ width: '100%', maxWidth: 400, animation: RISE }}>
      <h1 style={h1}>Too many attempts.</h1>
      <p style={sub}>
        Sign-in for <span style={{ color: 'var(--si-ink-2, #d4d4d8)' }}>{email || 'this account'}</span> is
        paused after repeated failed attempts. Your account has not been disabled.
      </p>

      {left > 0 ? (
        <p
          className="lm-num"
          style={{
            margin: '26px 0 0',
            fontSize: 15,
            color: 'var(--si-warn, #fbbf24)',
            fontFeatureSettings: "'tnum' 1",
          }}
        >
          Try again in {Math.floor(left / 60) > 0 ? `${Math.floor(left / 60)}m ` : ''}
          {left % 60}s
        </p>
      ) : (
        <p style={{ margin: '26px 0 0', fontSize: 15, color: 'var(--si-ok, #34d399)' }}>
          You can try again now.
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 22, marginTop: 30 }}>
        <button type="button" onClick={onBack} className="lm-si-ghost" style={ghost}>
          Back to sign in
        </button>
      </div>

      <p style={{ fontSize: 13, lineHeight: 1.65, margin: '30px 0 0', color: 'var(--si-ink-3, #71717a)' }}>
        If this keeps happening, reset your password, or ask your platform administrator to
        check for another system signing in with this address.
      </p>
    </section>
  );
}

const RISE = 'lm-rise 340ms cubic-bezier(0.2,0.7,0.2,1) both';
const h1 = {
  fontWeight: 600,
  fontSize: 'clamp(30px, 3vw, 41px)',
  lineHeight: 1.1,
  letterSpacing: '-0.04em',
  margin: 0,
  color: 'var(--si-ink, #fafafa)',
} as const;
const sub = { fontSize: 15, lineHeight: 1.6, margin: '14px 0 0', color: 'var(--si-sub, #a1a1aa)' } as const;
const ghost = {
  minHeight: 46,
  paddingInline: 22,
  border: '1px solid var(--si-line, #2f3646)',
  borderRadius: 10,
  background: 'var(--si-field, #12161f)',
  font: 'inherit',
  fontSize: 14.5,
  fontWeight: 500,
  color: 'var(--si-ink, #e4e4e7)',
  cursor: 'pointer',
  whiteSpace: 'nowrap' as const,
  flex: 'none',
} as const;
